<script>
import axios from 'axios';
import { sucesso } from '@/helpers/common/api';

/**
 * Modal de troca de senha do usuário autenticado.
 * Valida a senha atual + confirma a nova (2x), com mostrar/ocultar em cada campo.
 * Chama POST /auth/senha (backend valida a senha atual antes de gravar).
 */
export default {
  name: 'TrocarSenhaModal',
  props: {
    visivel: { type: Boolean, default: false },
  },
  emits: ['fechar'],
  data() {
    return {
      form: { senha_atual: '', senha_nova: '', senha_confirmacao: '' },
      mostrar: { atual: false, nova: false, conf: false },
      erro: '',
      salvando: false,
    };
  },
  watch: {
    visivel(val) {
      if (val) this.resetar();
    },
  },
  methods: {
    resetar() {
      this.form = { senha_atual: '', senha_nova: '', senha_confirmacao: '' };
      this.mostrar = { atual: false, nova: false, conf: false };
      this.erro = '';
      this.salvando = false;
    },
    validar() {
      const { senha_atual: atual, senha_nova: nova, senha_confirmacao: conf } = this.form;
      if (!atual || !nova || !conf) return 'Preencha todos os campos.';
      if (nova.length < 6) return 'A nova senha deve ter ao menos 6 caracteres.';
      if (nova !== conf) return 'A confirmação não confere com a nova senha.';
      if (nova === atual) return 'A nova senha deve ser diferente da atual.';
      return '';
    },
    async salvar() {
      const msg = this.validar();
      if (msg) { this.erro = msg; return; }
      this.erro = '';
      this.salvando = true;
      try {
        await axios.post('/auth/senha', {
          senha_atual: this.form.senha_atual,
          senha_nova: this.form.senha_nova,
          senha_confirmacao: this.form.senha_confirmacao,
        });
        sucesso({ title: 'Senha alterada com sucesso!' });
        this.fechar();
      } catch (e) {
        this.erro = e?.response?.data?.error || e?.message || 'Falha ao alterar a senha.';
      } finally {
        this.salvando = false;
      }
    },
    fechar() {
      this.$emit('fechar');
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <template v-if="visivel">
      <div class="modal fade show d-block trocar-senha-modal" tabindex="-1" @click.self="fechar">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title"><i class="ri-lock-password-line align-bottom me-1"></i> Mudar senha</h5>
              <button type="button" class="btn-close" @click="fechar"></button>
            </div>
            <form @submit.prevent="salvar">
              <div class="modal-body">
                <div v-if="erro" class="alert alert-danger py-2 small mb-3">{{ erro }}</div>

                <!-- Senha atual -->
                <div class="mb-3">
                  <label class="form-label">Senha atual <span class="text-danger">*</span></label>
                  <div class="input-group">
                    <input
                      v-model="form.senha_atual"
                      :type="mostrar.atual ? 'text' : 'password'"
                      class="form-control"
                      autocomplete="current-password"
                      placeholder="Sua senha atual"
                    />
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      :aria-label="mostrar.atual ? 'Ocultar senha' : 'Mostrar senha'"
                      @click="mostrar.atual = !mostrar.atual"
                    >
                      <i :class="mostrar.atual ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                    </button>
                  </div>
                </div>

                <!-- Nova senha -->
                <div class="mb-3">
                  <label class="form-label">Nova senha <span class="text-danger">*</span></label>
                  <div class="input-group">
                    <input
                      v-model="form.senha_nova"
                      :type="mostrar.nova ? 'text' : 'password'"
                      class="form-control"
                      autocomplete="new-password"
                      placeholder="Mínimo de 6 caracteres"
                    />
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      :aria-label="mostrar.nova ? 'Ocultar senha' : 'Mostrar senha'"
                      @click="mostrar.nova = !mostrar.nova"
                    >
                      <i :class="mostrar.nova ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                    </button>
                  </div>
                </div>

                <!-- Confirmar nova senha -->
                <div class="mb-1">
                  <label class="form-label">Confirmar nova senha <span class="text-danger">*</span></label>
                  <div class="input-group">
                    <input
                      v-model="form.senha_confirmacao"
                      :type="mostrar.conf ? 'text' : 'password'"
                      class="form-control"
                      autocomplete="new-password"
                      placeholder="Repita a nova senha"
                    />
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      :aria-label="mostrar.conf ? 'Ocultar senha' : 'Mostrar senha'"
                      @click="mostrar.conf = !mostrar.conf"
                    >
                      <i :class="mostrar.conf ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <BButton variant="ghost-dark" type="button" @click="fechar">Cancelar</BButton>
                <BButton variant="primary" type="submit" :disabled="salvando">
                  <BSpinner v-if="salvando" small class="me-1" />
                  {{ salvando ? 'Salvando...' : 'Alterar senha' }}
                </BButton>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show" @click="fechar"></div>
    </template>
  </Teleport>
</template>

<style scoped>
.trocar-senha-modal { z-index: 1055; }
.trocar-senha-modal + .modal-backdrop,
.modal-backdrop { z-index: 1050; cursor: pointer; }

/* Esconde o "olho" de revelar senha NATIVO do navegador (Edge/Chromium),
   já que temos o nosso próprio botão de mostrar/ocultar. */
.trocar-senha-modal input::-ms-reveal,
.trocar-senha-modal input::-ms-clear {
  display: none !important;
}
</style>
